import { setCookie, getCookie } from '../utils/cookie'

export default {
	// State
	currentMenu: null,
	cartOpen: false,
	filtersOpen: false,
	searchOpen: false,
	mobileMenuOpen: false,
	cartTimeline: null,
	showAnnouncement: true,
	introSeen: false,
	activeFilterCount: 0,
	availableMenus: [],
	previousCollectionPath: null,

	// Initialization
	init() {
		this.menuToggle = document.querySelector('[data-menu-toggle]')
		this.cartToggle = document.querySelector('[data-cart-toggle]')

		this.handleDrawerOutsideClick = this.handleDrawerOutsideClick.bind(this)
		this.handleKeyUp = this.handleKeyUp.bind(this)
		this.handleResize = this.handleResize.bind(this)
		this.onRouteChange = this.onRouteChange.bind(this)

		window.addEventListener('keyup', this.handleKeyUp)
		window.addEventListener('resize', this.handleResize)

		this.hydrateAnnouncement()
		this.hydrateIntroSeen()
		this.setupRouteListener()

		// Initialize previous collection path
		if (this.isCollectionRoute(window.location.pathname)) {
			this.previousCollectionPath = window.location.pathname
		}
	},

	// Global Handlers
	handleKeyUp(e) {
		if (e.key === 'Escape') {
			this.closeDrawer()
		}
	},

	handleResize() {
		const isMdAndUp = window.innerWidth >= 768 // Tailwind's md breakpoint

		if (isMdAndUp) {
			// Desktop view - close mobile menu if open
			if (this.mobileMenuOpen) {
				this.closeMobileMenu()
			}
		} else {
			// Mobile view - close desktop drawers if open
			if (this.currentMenu) {
				// Close desktop menu drawers
				this.currentMenu = null
				// Remove underlines from menu buttons
				document.querySelectorAll('.header-menu__link').forEach(btn => {
					btn.classList.remove('underline')
				})
			}
		}
	},

	handleDrawerOutsideClick(event) {
		// Check if click is on a toggle button
		const isToggleButton = event.target.closest('[data-menu-toggle], [data-cart-toggle]') || event.target.closest('.header-menu__link')
		if (isToggleButton) {
			return
		}

		// Re-query drawer elements on each click to account for teleported menus
		const drawerElements = document.querySelectorAll('[data-drawer]')

		// Check if click is inside any drawer
		const isInsideDrawer = Array.from(drawerElements).some(drawer => drawer.contains(event.target))

		if (!isInsideDrawer) {
			this.closeDrawer()
		}
	},

	// Centralized Drawer Management
	openDrawer(drawerType) {
		// This handles cart, filters, search, mobileMenu
		this[`${drawerType}Open`] = true

		// Then close the others after a brief delay to avoid flash
		setTimeout(() => {
			// Don't clear currentMenu when opening mobile menu
			if (drawerType !== 'menu' && drawerType !== 'mobileMenu') this.currentMenu = null
			if (drawerType !== 'cart') this.cartOpen = false
			if (drawerType !== 'filters') this.filtersOpen = false
			if (drawerType !== 'search') this.searchOpen = false
			if (drawerType !== 'mobileMenu') this.mobileMenuOpen = false

			if (this.hasAnyDrawerOpen) {
				document.documentElement.classList.add('no-scroll')
			} else {
				document.documentElement.classList.remove('no-scroll')
			}
		}, 200)

		requestAnimationFrame(() => {
			this.setupDrawerListeners()
		})
	},

	closeDrawer() {
		this.currentMenu = null
		this.cartOpen = false
		this.filtersOpen = false
		this.searchOpen = false
		this.mobileMenuOpen = false

		// Remove underlines from all menu buttons
		requestAnimationFrame(() => {
			document.querySelectorAll('.header-menu__link').forEach(btn => {
				btn.classList.remove('underline')
			})
			// Double-check with a tiny timeout to override any competing styles
			setTimeout(() => {
				document.querySelectorAll('.header-menu__link').forEach(btn => {
					btn.classList.remove('underline')
				})
			}, 10)
		})

		document.documentElement.classList.remove('no-scroll')

		requestAnimationFrame(() => {
			this.removeDrawerListeners()
		})
	},

	toggleDrawer(drawerType) {
		// Generic toggler for non-menu drawers
		if (this[`is${drawerType.charAt(0).toUpperCase() + drawerType.slice(1)}Open`]) {
			this.closeDrawer()
		} else {
			this.openDrawer(drawerType)
		}
	},

	setupDrawerListeners() {
		window.addEventListener('click', this.handleDrawerOutsideClick)
	},

	removeDrawerListeners() {
		window.removeEventListener('click', this.handleDrawerOutsideClick)
	},

	// Getters
	get isMenuOpen() {
		return !!this.currentMenu
	},

	get isCartOpen() {
		return this.cartOpen
	},

	get isFiltersOpen() {
		return this.filtersOpen
	},

	get isSearchOpen() {
		return this.searchOpen
	},

	get isMobileMenuOpen() {
		return this.mobileMenuOpen
	},

	get hasAnyDrawerOpen() {
		return this.isMenuOpen || this.cartOpen || this.filtersOpen || this.searchOpen || this.mobileMenuOpen
	},

	// Public API Methods
	toggleMenu(menuId) {
		if (this.currentMenu === menuId) {
			// Just close the current menu, don't affect other drawer states
			this.currentMenu = null
			// Remove styling from all buttons with a slight delay to ensure it sticks
			requestAnimationFrame(() => {
				document.querySelectorAll('.header-menu__link').forEach(btn => {
					btn.classList.remove('underline')
				})
				// Double-check with a tiny timeout to override any competing styles
				setTimeout(() => {
					document.querySelectorAll('.header-menu__link').forEach(btn => {
						btn.classList.remove('underline')
					})
				}, 10)
			})
		} else {
			// Just set the current menu, don't interfere with other drawers
			this.currentMenu = menuId
			// Update styling manually
			requestAnimationFrame(() => {
				document.querySelectorAll('.header-menu__link').forEach(btn => {
					const clickHandler = btn.getAttribute('@click')
					if (clickHandler && clickHandler.includes(menuId)) {
						btn.classList.add('underline')
					} else {
						btn.classList.remove('underline')
					}
				})
			})
		}

		// Only manage scroll lock and listeners if we're not in mobile menu mode
		if (!this.mobileMenuOpen) {
			if (this.currentMenu) {
				// Close other drawers only on desktop
				setTimeout(() => {
					this.cartOpen = false
					this.filtersOpen = false
					this.searchOpen = false

					if (this.hasAnyDrawerOpen) {
						document.documentElement.classList.add('no-scroll')
					}
				}, 200)

				requestAnimationFrame(() => {
					this.setupDrawerListeners()
				})
			} else {
				// Only remove listeners and scroll lock if no other drawers are open
				if (!this.hasAnyDrawerOpen) {
					document.documentElement.classList.remove('no-scroll')
					requestAnimationFrame(() => {
						this.removeDrawerListeners()
					})
				}
			}
		}
	},

	toggleCart() {
		this.toggleDrawer('cart')
	},

	openCart() {
		this.openDrawer('cart')
	},

	toggleFilters() {
		this.toggleDrawer('filters')
	},

	toggleSearch() {
		this.toggleDrawer('search')
	},

	toggleMobileMenu() {
		if (this.mobileMenuOpen) {
			// Closing mobile menu
			this.closeDrawer()
		} else {
			// Opening mobile menu - set first menu as active after a brief delay
			this.openDrawer('mobileMenu')
			if (this.availableMenus.length > 0 && !this.currentMenu) {
				// Use Alpine's nextTick to ensure proper reactivity
				// Use requestAnimationFrame for immediate but smooth execution
				requestAnimationFrame(() => {
					if (this.mobileMenuOpen && !this.currentMenu) {
						const firstMenuId = this.availableMenus[0]
						this.currentMenu = firstMenuId

						// Immediately trigger styling update
						const allMenuButtons = document.querySelectorAll('.header-menu__link')
						allMenuButtons.forEach(btn => {
							const clickHandler = btn.getAttribute('@click')
							if (clickHandler && clickHandler.includes(firstMenuId)) {
								btn.classList.add('underline')
							} else {
								btn.classList.remove('underline')
							}
						})
					}
				})
			}
		}
	},

	// Register a menu ID when menu components are initialized
	registerMenu(menuId, context = 'any') {
		if (!this.availableMenus.includes(menuId)) {
			this.availableMenus.push(menuId)
		}
	},

	// Get the first menu that should be active in mobile context
	getFirstMobileMenu() {
		// Prefer menus without -1 suffix, but fallback to any available menu
		const mobileMenus = this.availableMenus.filter(id => !id.includes('-1'))
		return mobileMenus.length > 0 ? mobileMenus[0] : this.availableMenus[0]
	},

	closeMenu() {
		if (this.isMenuOpen) this.closeDrawer()
	},

	closeCart() {
		if (this.isCartOpen) this.closeDrawer()
	},

	closeFilters() {
		if (this.isFiltersOpen) this.closeDrawer()
	},

	closeSearch() {
		if (this.isSearchOpen) this.closeDrawer()
	},

	closeMobileMenu() {
		if (this.isMobileMenuOpen) this.closeDrawer()
	},

	// Hydrate announcement state from cookie
	hydrateAnnouncement() {
		if (typeof document === 'undefined') return
		const dismissed = getCookie('district-vision-banner-dismissed')
		this.showAnnouncement = dismissed !== 'true'
	},

	// Hydrate introSeen state from cookie
	hydrateIntroSeen() {
		if (typeof document === 'undefined') return
		const seen = getCookie('district-vision-intro-seen')
		this.introSeen = seen === 'true'
	},

	// Mark intro as seen and persist in cookie
	setIntroSeen() {
		this.introSeen = true
		setCookie('district-vision-intro-seen', 'true')
	},

	// Announcement Methods
	dismissAnnouncement() {
		this.showAnnouncement = false
		setCookie('district-vision-banner-dismissed', 'true', 30)
	},

	get isAnnouncementVisible() {
		return this.showAnnouncement
	},

	// Route change handling
	setupRouteListener() {
		if (typeof window === 'undefined') return

		window.app?.swup?.hooks.on('content:replace', this.onRouteChange.bind(this))
	},

	onRouteChange() {
		const currentPath = window.location.pathname
		const previousPath = this.previousCollectionPath

		// Check if we're moving between different collection routes (not just query params)
		if (this.isCollectionRoute(previousPath) && this.isCollectionRoute(currentPath) && previousPath !== currentPath) {
			this.resetFilters()
		}

		// Update previous path for next comparison
		if (this.isCollectionRoute(currentPath)) {
			this.previousCollectionPath = currentPath
		}
	},

	isCollectionRoute(route) {
		return route && route.includes('/collections/')
	},

	resetFilters() {
		// Close filters if open
		if (this.filtersOpen) {
			this.filtersOpen = false
		}

		// Reset active filter count
		this.activeFilterCount = 0

		// Reset form if it exists
		const filterForm = document.querySelector('.collection-filters__form')
		if (filterForm) {
			// Reset to manual sort
			const manualSort = filterForm.querySelector('#Sort-manual')
			if (manualSort) {
				manualSort.checked = true
			}

			// Clear all checkboxes
			filterForm.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
				checkbox.checked = false
			})
		}
	},
}
